/**
 * Cloud Import Panel Component
 *
 * Structural clone of the drive-picker panel (split folder tree + content on
 * <app-slide-panel variant="full">) browsing a CONNECTED CLOUD ACCOUNT instead of Drive.
 * Three views:
 *   connect — provider buttons (enabled providers only, L-021) + the member's own
 *             connected accounts with in-panel disconnect;
 *   browse  — account picker, lazy folder tree, checkbox source selection, and the
 *             five-state chain (loading / revoked→Reconnect / error→Try again / empty / rows);
 *   started — live run progress via the subscription; closable anytime (the run is
 *             server-side and finishes with a notification).
 */

import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  effect,
  untracked,
  viewChild,
  DestroyRef
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCloudArrowDown,
  faFolder,
  faFolderOpen,
  faChevronDown,
  faChevronRight,
  faSpinnerThird,
  faImage,
  faFilm,
  faMusic,
  faFile,
  faLinkSlash,
  faCircleExclamation,
  faCircleCheck,
} from '@fortawesome/pro-duotone-svg-icons';
// Real provider brand marks — stated deviation from the duotone doctrine, Robert-approved
// 2026-07-02. Brand icons are single-layer, so they render via <fa-icon>.
import { faGoogleDrive, faMicrosoft, faDropbox } from '@fortawesome/free-brands-svg-icons';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { environment } from '../../../environments/environment';
import { CloudImportPanelService } from './cloud-import-panel.service';
import { CloudImportService } from '../../services/cloud-import.service';
import { LocaleService } from '../../services/locale.service';
import { SlidePanelComponent } from '../slide-panel/slide-panel.component';
import { LoadFailedComponent } from '../page-states/load-failed.component';
import { MutationError, GraphQLExecutionError, resolveErrorMessage } from '@branduals/graphql-errors';
import { formatFileSize } from '../../utils/file-details.utils';
import {
  CLOUD_PROVIDER_NAMES,
  type CloudProvider,
  type CloudConnection,
  type CloudFolderEntry,
  type CloudImportRun,
  type CloudImportSourceInput,
} from '../../models/cloud-import.models';

interface CloudTreeNode {
  /** Provider folder id; null = the account root. */
  id: string | null;
  label: string;
  expanded: boolean;
  children: CloudTreeNode[];
  childrenLoaded: boolean;
  loading: boolean;
  isRoot?: boolean;
}

type BrowseState = 'loading' | 'revoked' | 'error' | 'ready';

@Component({
  selector: 'app-cloud-import-panel',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    FontAwesomeModule,
    ProgressSpinnerModule,
    ProgressBarModule,
    CheckboxModule,
    SelectModule,
    ButtonModule,
    TranslateModule,
    SlidePanelComponent,
    LoadFailedComponent
  ],
  templateUrl: './cloud-import-panel.component.html',
  styleUrl: './cloud-import-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CloudImportPanelComponent {
  private readonly slidePanel = viewChild(SlidePanelComponent);

  private readonly panelService = inject(CloudImportPanelService);
  private readonly cloudImportService = inject(CloudImportService);
  private readonly localeService = inject(LocaleService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  // Icons
  readonly faCloudArrowDown = faCloudArrowDown;
  readonly faFolder = faFolder;
  readonly faFolderOpen = faFolderOpen;
  readonly faChevronDown = faChevronDown;
  readonly faChevronRight = faChevronRight;
  readonly faSpinnerThird = faSpinnerThird;
  readonly faLinkSlash = faLinkSlash;
  readonly faCircleExclamation = faCircleExclamation;
  readonly faCircleCheck = faCircleCheck;

  /**
   * Provider name/mark lookups are METHODS, not field captures: a class-field
   * `= CLOUD_PROVIDER_NAMES` evaluates at module/construction time, and the shared
   * models chunk can still be mid-evaluation when spec bundles load in one worker
   * (esbuild chunk-order artifact — providerNames arrived undefined in the full
   * ng test run). Call-time reads always see the fully-evaluated binding.
   * Brand marks (the only sanctioned <fa-icon> usage in this component).
   */
  providerName(provider: CloudProvider): string {
    return CLOUD_PROVIDER_NAMES[provider];
  }

  providerMark(provider: CloudProvider): IconDefinition {
    switch (provider) {
      case 'GOOGLE_DRIVE': return faGoogleDrive;
      case 'ONE_DRIVE': return faMicrosoft;
      case 'DROPBOX': return faDropbox;
    }
  }

  // The connect view's data-use notice links the privacy policy, which lives on the
  // brand-less marketing site: a brand host (subdomain or custom domain) has no /privacy
  // route, and the marketing origin differs per environment.
  protected readonly privacyUrl = `${environment.marketingBaseUrl}/privacy`;

  // Panel state from services
  readonly isOpen = this.panelService.isOpen;
  readonly context = this.panelService.context;
  readonly connections = this.cloudImportService.connections;
  readonly enabledProviders = this.cloudImportService.enabledProviders;
  readonly connectionsLoading = this.cloudImportService.connectionsLoading;
  readonly connecting = this.cloudImportService.connecting;

  // View state
  readonly view = signal<'connect' | 'browse' | 'started'>('connect');
  readonly selectedConnectionId = signal<string | null>(null);

  // Browse state
  readonly folderTree = signal<CloudTreeNode[]>([]);
  readonly currentFolderId = signal<string | null>(null);
  readonly breadcrumbs = signal<{ id: string | null; name: string }[]>([]);
  readonly entries = signal<CloudFolderEntry[]>([]);
  readonly browseState = signal<BrowseState>('loading');

  // Selection — keyed by provider externalId, persists across folder navigation.
  readonly selection = signal<Map<string, CloudImportSourceInput>>(new Map());
  readonly selectionCount = computed(() => this.selection().size);
  readonly selectionHasFolder = computed(() => {
    for (const source of this.selection().values()) {
      if (source.isFolder) return true;
    }
    return false;
  });

  // Start / started state
  readonly starting = signal(false);
  readonly startedRun = signal<CloudImportRun | null>(null);

  readonly startedProgress = computed(() => {
    const run = this.startedRun();
    if (!run || run.totalFiles === 0) return 0;
    const processed = run.importedFiles + run.skippedFiles + run.failedFiles;
    return Math.min(100, Math.round((processed / run.totalFiles) * 100));
  });
  readonly startedIsActive = computed(() => {
    const run = this.startedRun();
    return !!run && (run.status === 'PENDING' || run.status === 'ENUMERATING' || run.status === 'TRANSFERRING');
  });

  readonly connectionOptions = computed(() =>
    this.connections().map(c => ({
      label: `${CLOUD_PROVIDER_NAMES[c.provider]} — ${c.accountLabel}`,
      value: c.id,
    })));

  /**
   * The account picker lives INSIDE the slide-panel (z-index 1150). PrimeNG's select overlay is
   * body-appended and uses the "overlay" z-index category — effective z-index =
   * `baseZIndex + config.zIndex.overlay` (1000). With the default baseZIndex (0) the dropdown opens
   * at ~1000, BEHIND the 1150 panel, so with 2+ accounts it looked unresponsive (the list was hidden
   * under the panel). Lift it to 175 + 1000 = 1175 — clear of the 1150 panel, below the 1200 preview
   * band. Applies to the browse-view p-select only.
   */
  protected readonly accountOverlayOptions = { baseZIndex: 175 };

  // Load on open: trigger signal read FIRST, body in untracked (an effect tracks every signal
  // its synchronous call stack reads — the loader would re-arm it otherwise, playbook §4).
  private readonly loadEffect = effect(() => {
    const isOpen = this.isOpen();
    untracked(() => {
      if (isOpen) void this.onPanelOpened();
    });
  });

  // Bridge the service's open-state to the shared <app-slide-panel> (drive-picker :216-219).
  private readonly openBridge = effect(() => {
    const p = this.slidePanel();
    if (p) (this.isOpen() ? p.open() : p.close());
  });

  constructor() {
    // Live progress for the started view: the service already maintains activeRuns; here we
    // only mirror snapshots of OUR run onto the local startedRun (counters + status).
    this.cloudImportService.runChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(snapshot => {
        const run = this.startedRun();
        if (!run || run.id !== snapshot.runId) return;
        this.startedRun.set({
          ...run,
          status: snapshot.status,
          totalFiles: snapshot.totalFiles,
          importedFiles: snapshot.importedFiles,
          skippedFiles: snapshot.skippedFiles,
          failedFiles: snapshot.failedFiles,
          totalBytes: snapshot.totalBytes,
          transferredBytes: snapshot.transferredBytes,
          errorCode: snapshot.errorCode,
          completedAt: snapshot.completedAt,
        });
      });
  }

  private async onPanelOpened(): Promise<void> {
    this.resetBrowseState();
    this.startedRun.set(null);
    this.cloudImportService.subscribeToChanges();
    await this.cloudImportService.loadConnections();
    const connections = this.connections();
    if (connections.length === 0) {
      this.view.set('connect');
    } else {
      await this.enterBrowse(connections[0].id);
    }
  }

  close(): void {
    this.panelService.close();
    // Reset after the close animation (drive-picker pattern).
    setTimeout(() => {
      if (!this.isOpen()) {
        this.resetBrowseState();
        this.view.set('connect');
        this.startedRun.set(null);
      }
    }, 250);
  }

  private resetBrowseState(): void {
    this.folderTree.set([]);
    this.currentFolderId.set(null);
    this.breadcrumbs.set([]);
    this.entries.set([]);
    this.selection.set(new Map());
    this.browseState.set('loading');
    this.selectedConnectionId.set(null);
  }

  // -------------------------------------------------------------------------
  // Connect view
  // -------------------------------------------------------------------------

  async onConnect(provider: CloudProvider): Promise<void> {
    try {
      const outcome = await this.cloudImportService.connect(provider);
      switch (outcome.status) {
        case 'success': {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('cloudImport.connect.connected', {
              account: outcome.accountLabel ?? CLOUD_PROVIDER_NAMES[provider],
            }),
            life: 3000
          });
          // The service refetched connections — enter browse on the new account.
          const match = this.connections().find(c =>
            c.provider === provider && c.accountLabel === outcome.accountLabel);
          const target = match ?? this.connections()[0];
          if (target) await this.enterBrowse(target.id);
          break;
        }
        case 'error':
          this.toastError(outcome.errorCode ?? 'CLOUD_PROVIDER_ERROR');
          break;
        case 'blocked':
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('cloudImport.connect.popupBlocked'),
            life: 5000
          });
          break;
        case 'cancelled':
          break; // quiet — the user changed their mind
      }
    } catch (error) {
      this.toastCaught(error);
    }
  }

  async onDisconnect(connection: CloudConnection): Promise<void> {
    try {
      await this.cloudImportService.disconnect(connection.id);
      if (this.selectedConnectionId() === connection.id) {
        this.resetBrowseState();
      }
    } catch (error) {
      this.toastCaught(error);
    }
  }

  showConnectView(): void {
    this.view.set('connect');
  }

  backToBrowse(): void {
    const id = this.selectedConnectionId() ?? this.connections()[0]?.id;
    if (id) void this.enterBrowse(id);
  }

  // -------------------------------------------------------------------------
  // Browse view
  // -------------------------------------------------------------------------

  async enterBrowse(connectionId: string): Promise<void> {
    this.view.set('browse');
    this.selectedConnectionId.set(connectionId);
    this.selection.set(new Map());
    await this.loadRoot();
  }

  async onConnectionPicked(connectionId: string): Promise<void> {
    if (connectionId === this.selectedConnectionId()) return;
    await this.enterBrowse(connectionId);
  }

  private rootLabel(): string {
    const connection = this.connections().find(c => c.id === this.selectedConnectionId());
    return connection ? CLOUD_PROVIDER_NAMES[connection.provider] : '';
  }

  private async loadRoot(): Promise<void> {
    const root: CloudTreeNode = {
      id: null,
      label: this.rootLabel(),
      expanded: true,
      children: [],
      childrenLoaded: false,
      loading: false,
      isRoot: true,
    };
    this.folderTree.set([root]);
    this.currentFolderId.set(null);
    this.breadcrumbs.set([{ id: null, name: root.label }]);
    await this.loadFolderContents(null);
    // Seed the root's tree children from the loaded entries (folders only) — saves a round-trip.
    if (this.browseState() === 'ready') {
      root.children = this.entries().filter(e => e.isFolder).map(e => this.toTreeNode(e));
      root.childrenLoaded = true;
      this.folderTree.update(tree => [...tree]);
    }
  }

  private toTreeNode(entry: CloudFolderEntry): CloudTreeNode {
    return { id: entry.id, label: entry.name, expanded: false, children: [], childrenLoaded: false, loading: false };
  }

  private async loadFolderContents(folderId: string | null): Promise<void> {
    const connectionId = this.selectedConnectionId();
    if (!connectionId) return;
    this.browseState.set('loading');
    try {
      const entries = await this.cloudImportService.browseChildren(connectionId, folderId);
      // Folders first (Drive content partition); provider order preserved within each group.
      this.entries.set([...entries.filter(e => e.isFolder), ...entries.filter(e => !e.isFolder)]);
      this.browseState.set('ready');
    } catch (error) {
      this.entries.set([]);
      if (error instanceof GraphQLExecutionError && error.hasCode('CLOUD_CONNECTION_REVOKED')) {
        this.browseState.set('revoked');
      } else {
        console.error('[CloudImportPanel] Browse failed:', error);
        this.browseState.set('error');
      }
    }
  }

  async retryBrowse(): Promise<void> {
    await this.loadFolderContents(this.currentFolderId());
  }

  async onTreeNodeClick(node: CloudTreeNode): Promise<void> {
    if (!node.childrenLoaded) {
      await this.loadTreeNodeChildren(node);
    }
    node.expanded = !node.expanded;
    this.folderTree.update(tree => [...tree]);

    this.currentFolderId.set(node.id);
    const path = this.findNodePath(node.id);
    this.breadcrumbs.set(path ?? [{ id: node.id, name: node.label }]);
    await this.loadFolderContents(node.id);
  }

  private async loadTreeNodeChildren(node: CloudTreeNode): Promise<void> {
    const connectionId = this.selectedConnectionId();
    if (!connectionId) return;
    node.loading = true;
    this.folderTree.update(tree => [...tree]);
    try {
      const entries = await this.cloudImportService.browseChildren(connectionId, node.id);
      node.children = entries.filter(e => e.isFolder).map(e => this.toTreeNode(e));
      node.childrenLoaded = true;
    } catch (error) {
      console.error('[CloudImportPanel] Tree children failed:', error);
    } finally {
      node.loading = false;
      this.folderTree.update(tree => [...tree]);
    }
  }

  private findNodePath(id: string | null): { id: string | null; name: string }[] | null {
    const walk = (nodes: CloudTreeNode[], trail: CloudTreeNode[]): CloudTreeNode[] | null => {
      for (const n of nodes) {
        const next = [...trail, n];
        if (n.id === id) return next;
        const found = walk(n.children, next);
        if (found) return found;
      }
      return null;
    };
    const path = walk(this.folderTree(), []);
    return path ? path.map(n => ({ id: n.id, name: n.label })) : null;
  }

  async navigateToFolder(entry: CloudFolderEntry): Promise<void> {
    const crumbs = this.breadcrumbs();
    const existingIdx = crumbs.findIndex(c => c.id === entry.id);
    if (existingIdx >= 0) {
      this.breadcrumbs.set(crumbs.slice(0, existingIdx + 1));
    } else {
      this.breadcrumbs.update(b => [...b, { id: entry.id, name: entry.name }]);
    }
    this.currentFolderId.set(entry.id);
    await this.loadFolderContents(entry.id);
  }

  async onBreadcrumbClick(crumb: { id: string | null; name: string }): Promise<void> {
    const crumbs = this.breadcrumbs();
    const idx = crumbs.findIndex(c => c.id === crumb.id);
    if (idx >= 0) this.breadcrumbs.set(crumbs.slice(0, idx + 1));
    this.currentFolderId.set(crumb.id);
    await this.loadFolderContents(crumb.id);
  }

  onRowClick(entry: CloudFolderEntry): void {
    if (entry.isFolder) {
      void this.navigateToFolder(entry);
    } else {
      this.toggleEntry(entry);
    }
  }

  isTreeNodeActive(node: CloudTreeNode): boolean {
    return this.currentFolderId() === node.id;
  }

  // -------------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------------

  isSelected(entry: CloudFolderEntry): boolean {
    return this.selection().has(entry.id);
  }

  toggleEntry(entry: CloudFolderEntry): void {
    const next = new Map(this.selection());
    if (next.has(entry.id)) {
      next.delete(entry.id);
    } else {
      // parentPath = the breadcrumb names below the provider root — display context for
      // the history failure list.
      const parentPath = this.breadcrumbs().slice(1).map(c => c.name).join('/');
      next.set(entry.id, {
        externalId: entry.id,
        name: entry.name,
        isFolder: entry.isFolder,
        mimeType: entry.mimeType || null,
        sizeBytes: entry.sizeBytes,
        parentPath: parentPath || null,
      });
    }
    this.selection.set(next);
  }

  // -------------------------------------------------------------------------
  // Start import / started view
  // -------------------------------------------------------------------------

  async startImport(): Promise<void> {
    const context = this.context();
    const connectionId = this.selectedConnectionId();
    if (!context || !connectionId || this.selectionCount() === 0) return;

    // PESSIMISTIC by design (optimistic-UX exception 2): the manifest and counts are
    // server-determined — spinner, then the returned run seeds the started view.
    this.starting.set(true);
    try {
      const run = await this.cloudImportService.startImport({
        connectionId,
        destinationRecordId: context.destinationRecordId,
        titleLocale: this.localeService.getActiveContentLanguage(),
        sources: Array.from(this.selection().values()),
      });
      this.startedRun.set(run);
      this.view.set('started');
    } catch (error) {
      this.toastCaught(error);
    } finally {
      this.starting.set(false);
    }
  }

  async cancelStartedRun(): Promise<void> {
    const run = this.startedRun();
    if (!run) return;
    try {
      await this.cloudImportService.cancelImport(run.id);
      this.startedRun.update(r => r ? { ...r, status: 'CANCELED' } : r);
    } catch (error) {
      this.toastCaught(error);
    }
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  entryIcon(entry: CloudFolderEntry): IconDefinition {
    if (entry.isFolder) return faFolder;
    const mime = entry.mimeType ?? '';
    if (mime.startsWith('image/')) return faImage;
    if (mime.startsWith('video/')) return faFilm;
    if (mime.startsWith('audio/')) return faMusic;
    return faFile;
  }

  entrySize(entry: CloudFolderEntry): string {
    return entry.sizeBytes != null ? formatFileSize(entry.sizeBytes, this.localeService.activeUILanguage()) : '';
  }

  private toastError(code: string): void {
    this.messageService.add({
      severity: 'error',
      summary: resolveErrorMessage(
        { code, message: '' },
        (k, p) => this.translateService.instant(k, p)),
    });
  }

  private toastCaught(error: unknown): void {
    const detail = error instanceof MutationError
      ? error.userErrors
          .map(u => resolveErrorMessage(u, (k, p) => this.translateService.instant(k, p)))
          .join('; ')
      : this.translateService.instant('common.errorGeneric');
    this.messageService.add({ severity: 'error', summary: detail });
  }
}
